import { useEffect, useRef, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePlayer } from '@/hooks/usePlayer'


// ─── Constants ────────────────────────────────────────────────────────────────
const PINK = '#ff2680'
const GOLD = '#ffd700'
const BG = '#0a0008'
const TRAIL_LENGTH = 18

// ─── Types ────────────────────────────────────────────────────────────────────
type LevelKey = 'easy' | 'medium' | 'hard'
type EnemyType = 'normal' | 'fast' | 'zigzag' | 'big'
type GamePhase = 'menu' | 'playing' | 'dead'

interface LevelConfig {
  label: string
  color: string
  initSpeed: number
  maxSpeed: number
  speedGrowth: number
  spawnIntervalStart: number
  spawnIntervalMin: number
  countBase: number
  countMax: number
  zigzagAmp: number
  typeWeights: [number, number, number, number]
  invincibleFrames: number
  lives: number
  scoreMulti: number
}
interface Star { x: number; y: number; size: number; speed: number; alpha: number }
interface GridLine { y: number; speed: number; alpha: number }
interface TrailPoint { x: number; y: number }

// ─── Level Definitions ────────────────────────────────────────────────────────
const LEVELS: Record<LevelKey, LevelConfig> = {
  easy: {
    label: 'EASY', color: '#00ff88', initSpeed: 1.8, maxSpeed: 5, speedGrowth: 0.02,
    spawnIntervalStart: 100, spawnIntervalMin: 55, countBase: 1, countMax: 3,
    zigzagAmp: 1.5, typeWeights: [0.6, 0.2, 0.15, 0.05], invincibleFrames: 150, lives: 3, scoreMulti: 1,
  },
  medium: {
    label: 'MEDIUM', color: GOLD, initSpeed: 2.5, maxSpeed: 8, speedGrowth: 0.035,
    spawnIntervalStart: 80, spawnIntervalMin: 32, countBase: 1, countMax: 5,
    zigzagAmp: 2.5, typeWeights: [0.4, 0.25, 0.25, 0.1], invincibleFrames: 100, lives: 3, scoreMulti: 2,
  },
  hard: {
    label: 'HARD', color: PINK, initSpeed: 3.5, maxSpeed: 12, speedGrowth: 0.06,
    spawnIntervalStart: 55, spawnIntervalMin: 20, countBase: 2, countMax: 7,
    zigzagAmp: 3.5, typeWeights: [0.2, 0.3, 0.3, 0.2], invincibleFrames: 70, lives: 2, scoreMulti: 3,
  },
}

// ─── Utility ─────────────────────────────────────────────────────────────────
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
const rand = (a: number, b: number) => a + Math.random() * (b - a)
const randInt = (a: number, b: number) => Math.floor(rand(a, b))

// ─── Particle ─────────────────────────────────────────────────────────────────
class Particle {
  x: number; y: number; vx: number; vy: number
  color: string; life: number; maxLife: number; size: number
  constructor(x: number, y: number, vx: number, vy: number, color: string, life: number) {
    this.x = x; this.y = y; this.vx = vx; this.vy = vy
    this.color = color; this.life = life; this.maxLife = life; this.size = rand(2, 5)
  }
  update() { this.x += this.vx; this.y += this.vy; this.vy += 0.12; this.vx *= 0.97; this.life-- }
  draw(ctx: CanvasRenderingContext2D) {
    const alpha = this.life / this.maxLife
    ctx.save(); ctx.globalAlpha = alpha; ctx.fillStyle = this.color
    ctx.fillRect(this.x - this.size / 2, this.y - this.size / 2, this.size, this.size)
    ctx.restore()
  }
}

// ─── Enemy ────────────────────────────────────────────────────────────────────
class Enemy {
  x: number; y: number; speed: number; type: EnemyType
  size: number; rotation: number; rotSpeed: number
  color: string; alive: boolean; wobble: number; cfg: LevelConfig
  constructor(x: number, speed: number, type: EnemyType, cfg: LevelConfig) {
    this.x = x; this.y = -30; this.speed = speed; this.type = type
    this.size = type === 'big' ? 23 : type === 'fast' ? 10 : 16
    this.rotation = 0; this.rotSpeed = rand(-0.06, 0.06)
    this.color = type === 'big' ? PINK : type === 'fast' ? '#ff6eb4' : '#cc1060'
    this.alive = true; this.wobble = rand(0, Math.PI * 2); this.cfg = cfg
  }
  update(W: number, H: number) {
    this.y += this.speed; this.rotation += this.rotSpeed; this.wobble += 0.05
    if (this.type === 'zigzag') {
      this.x += Math.sin(this.wobble) * this.cfg.zigzagAmp
      this.x = clamp(this.x, this.size, W - this.size)
    }
    if (this.y > H + 60) this.alive = false
  }
  draw(ctx: CanvasRenderingContext2D) {
    ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.rotation)
    ctx.shadowColor = this.color; ctx.shadowBlur = 14; ctx.fillStyle = this.color
    const s = this.size
    ctx.beginPath()
    if (this.type === 'big') {
      ctx.moveTo(0, -s); ctx.lineTo(s * 0.7, s * 0.5); ctx.lineTo(0, s * 0.2); ctx.lineTo(-s * 0.7, s * 0.5)
    } else if (this.type === 'fast') {
      ctx.moveTo(0, -s); ctx.lineTo(s, s); ctx.lineTo(0, s * 0.4); ctx.lineTo(-s, s)
    } else {
      for (let i = 0; i < 4; i++) {
        const angle = (i / 4) * Math.PI * 2 - Math.PI / 4
        const r = i % 2 === 0 ? s : s * 0.5
        if (i === 0) ctx.moveTo(Math.cos(angle) * r, Math.sin(angle) * r)
        else ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r)
      }
    }
    ctx.closePath(); ctx.fill(); ctx.restore()
  }
  hits(px: number, py: number, pr: number) {
    const dx = this.x - px, dy = this.y - py
    return Math.sqrt(dx * dx + dy * dy) < this.size * 0.85 + pr
  }
}

// ─── GameState ────────────────────────────────────────────────────────────────
class GameState {
  px: number; py: number; pr: number; targetX: number
  trail: TrailPoint[]; enemies: Enemy[]; particles: Particle[]
  stars: Star[]; gridLines: GridLine[]
  score: number; lives: number; dead: boolean
  invincible: number; dashCool: number; dashActive: number
  spawnTimer: number; speed: number; keys: Record<string, boolean>
  shakeX: number; shakeY: number; mult: number; multTimer: number
  frame: number; pulse: number; waveCount: number
  W: number; H: number; cfg: LevelConfig; levelKey: LevelKey

  constructor(W: number, H: number, levelKey: LevelKey) {
    this.W = W; this.H = H; this.cfg = LEVELS[levelKey]; this.levelKey = levelKey
    this.px = W / 2; this.py = H - 80; this.pr = 12; this.targetX = W / 2
    this.trail = []; this.enemies = []; this.particles = []
    this.score = 0; this.lives = this.cfg.lives; this.dead = false
    this.invincible = 0; this.dashCool = 0; this.dashActive = 0
    this.spawnTimer = 0; this.speed = this.cfg.initSpeed; this.keys = {}
    this.shakeX = 0; this.shakeY = 0; this.mult = 1; this.multTimer = 0
    this.frame = 0; this.pulse = 0; this.waveCount = 0
    this.stars = this._makeStars(); this.gridLines = this._makeGrid()
  }
  private _makeStars(): Star[] {
    const s: Star[] = []
    for (let i = 0; i < 60; i++) s.push({ x: rand(0, this.W), y: rand(0, this.H), size: rand(0.5, 2.5), speed: rand(0.3, 1.5), alpha: rand(0.2, 0.8) })
    return s
  }
  private _makeGrid(): GridLine[] {
    const g: GridLine[] = []
    for (let i = 0; i < 20; i++) g.push({ y: rand(0, this.H), speed: rand(0.3, 1.2), alpha: rand(0.03, 0.12) })
    return g
  }
  resize(W: number, H: number) {
    this.W = W; this.H = H; this.py = H - 80
    this.px = clamp(this.px, this.pr, W - this.pr)
    this.targetX = clamp(this.targetX, this.pr, W - this.pr)
  }
  explode(x: number, y: number, color: string, count: number) {
    for (let i = 0; i < count; i++) {
      const angle = rand(0, Math.PI * 2), spd = rand(2, 9)
      this.particles.push(new Particle(x, y, Math.cos(angle) * spd, Math.sin(angle) * spd - rand(1, 4), color, randInt(20, 45)))
    }
  }
  shake(amt: number) { this.shakeX = (Math.random() - 0.5) * amt; this.shakeY = (Math.random() - 0.5) * amt }
  dash() {
    if (this.dashCool === 0 && !this.dead) { this.dashActive = 14; this.dashCool = 90; this.explode(this.px, this.py, GOLD, 10) }
  }
  spawnWave() {
    this.waveCount++
    const cfg = this.cfg, diff = Math.min(this.waveCount * 0.09, 5)
    const count = clamp(cfg.countBase + Math.floor(diff * 0.9), 1, cfg.countMax)
    const types: EnemyType[] = ['normal', 'fast', 'zigzag', 'big']
    for (let i = 0; i < count; i++) {
      const r = Math.random(); let acc = 0, typeIdx = 0
      for (let j = 0; j < cfg.typeWeights.length; j++) { acc += cfg.typeWeights[j]; if (r < acc) { typeIdx = j; break } }
      const spd = clamp(this.speed + rand(-0.3, 0.6) + diff * 0.3, 1.2, cfg.maxSpeed)
      this.enemies.push(new Enemy(rand(30, this.W - 30), spd, types[typeIdx], cfg))
    }
  }
  update() {
    this.frame++; this.pulse += 0.04; this.shakeX *= 0.8; this.shakeY *= 0.8
    if (this.dashCool > 0) this.dashCool--
    if (this.dashActive > 0) this.dashActive--
    if (this.invincible > 0) this.invincible--
    if (this.multTimer > 0) { this.multTimer--; if (this.multTimer === 0) this.mult = 1 }
    const spd = this.dashActive > 0 ? 11 : 5.5
    if (this.keys['ArrowLeft'] || this.keys['a'] || this.keys['A']) this.targetX -= spd
    if (this.keys['ArrowRight'] || this.keys['d'] || this.keys['D']) this.targetX += spd
    this.targetX = clamp(this.targetX, this.pr + 4, this.W - this.pr - 4)
    this.px = lerp(this.px, this.targetX, 0.18)
    this.px = clamp(this.px, this.pr + 2, this.W - this.pr - 2)
    this.trail.unshift({ x: this.px, y: this.py })
    if (this.trail.length > TRAIL_LENGTH) this.trail.pop()
    const interval = Math.max(this.cfg.spawnIntervalStart - this.waveCount * 2, this.cfg.spawnIntervalMin)
    if (++this.spawnTimer >= interval) { this.spawnTimer = 0; this.spawnWave(); this.speed = Math.min(this.speed + this.cfg.speedGrowth, this.cfg.maxSpeed) }
    for (const e of this.enemies) e.update(this.W, this.H)
    for (const p of this.particles) p.update()
    this.particles = this.particles.filter(p => p.life > 0)
    this.enemies = this.enemies.filter(e => e.alive)
    for (const s of this.stars) { s.y += s.speed; if (s.y > this.H) { s.y = -5; s.x = rand(0, this.W) } }
    for (const g of this.gridLines) { g.y += g.speed; if (g.y > this.H) g.y = 0 }
  }
  checkCollision() {
    if (this.invincible > 0) return false
    for (const e of this.enemies) {
      if (e.alive && e.hits(this.px, this.py, this.pr)) {
        e.alive = false; this.lives--; this.invincible = this.cfg.invincibleFrames
        this.shake(18); this.explode(this.px, this.py, PINK, 22); this.explode(e.x, e.y, e.color, 12)
        return true
      }
    }
    return false
  }
  draw(ctx: CanvasRenderingContext2D) {
    const { W, H } = this
    ctx.fillStyle = BG; ctx.fillRect(0, 0, W, H)
    for (const g of this.gridLines) {
      ctx.strokeStyle = `rgba(255,38,128,${g.alpha})`; ctx.lineWidth = 0.5
      ctx.beginPath(); ctx.moveTo(0, g.y); ctx.lineTo(W, g.y); ctx.stroke()
    }
    ctx.strokeStyle = 'rgba(255,38,128,0.06)'; ctx.lineWidth = 0.5
    for (let i = 0; i <= Math.ceil(W / 60); i++) {
      ctx.beginPath(); ctx.moveTo(i * 60, 0); ctx.lineTo(i * 60, H); ctx.stroke()
    }
    for (const s of this.stars) { ctx.globalAlpha = s.alpha; ctx.fillStyle = '#fff'; ctx.fillRect(s.x, s.y, s.size, s.size) }
    ctx.globalAlpha = 1
    for (const e of this.enemies) e.draw(ctx)
    for (const p of this.particles) p.draw(ctx)
    const inv = this.invincible > 0 && Math.floor(this.frame / 5) % 2 === 0
    if (!inv) {
      for (let i = this.trail.length - 1; i >= 0; i--) {
        const t = this.trail[i]
        ctx.save(); ctx.globalAlpha = (1 - i / this.trail.length) * 0.28
        ctx.fillStyle = this.dashActive > 0 ? GOLD : PINK
        ctx.beginPath(); ctx.arc(t.x, t.y, 18 * (1 - (i / this.trail.length) * 0.65) * 0.55, 0, Math.PI * 2); ctx.fill(); ctx.restore()
      }
      ctx.globalAlpha = 1
      ctx.save()
      ctx.shadowColor = this.dashActive > 0 ? GOLD : PINK
      ctx.shadowBlur = (10 + Math.sin(this.pulse) * 4) * 2.5
      ctx.fillStyle = this.dashActive > 0 ? 'rgba(255,215,0,0.25)' : 'rgba(255,38,128,0.25)'
      ctx.beginPath(); ctx.arc(this.px, this.py, this.pr + 4, 0, Math.PI * 2); ctx.fill(); ctx.restore()
      ctx.save(); ctx.strokeStyle = this.dashActive > 0 ? GOLD : PINK; ctx.lineWidth = 1.5
      ctx.globalAlpha = 0.55 + Math.sin(this.pulse) * 0.2
      ctx.beginPath(); ctx.arc(this.px, this.py, this.pr + 7 + Math.sin(this.pulse) * 2, 0, Math.PI * 2); ctx.stroke(); ctx.restore()
      ctx.globalAlpha = 1
      const bobY = Math.sin(this.pulse * 1.4) * 2.5
      const tilt = this.dashActive > 0 ? -0.3 : Math.sin(this.pulse * 0.8) * 0.08
      const scale = this.dashActive > 0 ? 1.18 : 1 + Math.sin(this.pulse * 1.4) * 0.04
      ctx.save(); ctx.translate(this.px, this.py + bobY); ctx.rotate(tilt); ctx.scale(scale, scale)
      ctx.font = '28px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.fillText('🦜', 0, 0); ctx.restore()
    }
  }
}

// ─── ParrotSave Component ─────────────────────────────────────────────────────
export default function ParrotSave() {
  const navigate = useNavigate()
  const { submitScore } = usePlayer()          // ADD
  const submitScoreRef = useRef(submitScore)   // ADD
  useEffect(() => { submitScoreRef.current = submitScore }, [submitScore])  // ADD

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const gsRef = useRef<GameState | null>(null)
  const phaseRef = useRef<GamePhase>('menu')
  const animRef = useRef<number>(0)
  const scoreRef = useRef<number>(0)
  const bestRef = useRef<number>(0)
  const selectedLevelRef = useRef<LevelKey>('easy')

  const scoreElRef = useRef<HTMLSpanElement>(null)
  const bestElRef = useRef<HTMLSpanElement>(null)
  const livesElRef = useRef<HTMLSpanElement>(null)
  const multElRef = useRef<HTMLDivElement>(null)
  const dangerElRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const finalScoreElRef = useRef<HTMLDivElement>(null)
  const bestLineElRef = useRef<HTMLDivElement>(null)
  const scoreSectionRef = useRef<HTMLDivElement>(null)
  const playBtnRef = useRef<HTMLButtonElement>(null)
  const dashBarRef = useRef<HTMLDivElement>(null)
  const dashFillRef = useRef<HTMLDivElement>(null)
  const dashLabelRef = useRef<HTMLSpanElement>(null)
  const levelBadgeRef = useRef<HTMLDivElement>(null)

  const [selectedLevel, setSelectedLevel] = useState<LevelKey>('easy')

  const mono = "'JetBrains Mono', 'Courier New', monospace"

  const setLives = useCallback((n: number) => {
    const hearts = ['', '♥', '♥ ♥', '♥ ♥ ♥']
    if (livesElRef.current) { livesElRef.current.textContent = hearts[Math.max(0, n)] ?? ''; livesElRef.current.style.color = n === 1 ? PINK : '#fff' }
  }, [])
  const setMult = useCallback((m: number) => {
    if (!multElRef.current) return
    if (m > 1) { multElRef.current.textContent = `× ${m} MULTIPLIER`; multElRef.current.style.opacity = '1' } else multElRef.current.style.opacity = '0'
  }, [])
  const flashDanger = useCallback(() => {
    if (!dangerElRef.current) return
    dangerElRef.current.style.opacity = '0.55'; dangerElRef.current.style.borderWidth = '7px'
    setTimeout(() => { if (dangerElRef.current) { dangerElRef.current.style.opacity = '0'; dangerElRef.current.style.borderWidth = '0px' } }, 380)
  }, [])
  const showOverlay = useCallback((isGameOver: boolean, finalScoreVal?: number) => {
    if (!overlayRef.current) return
    overlayRef.current.style.display = 'flex'
    if (isGameOver && scoreSectionRef.current && finalScoreElRef.current && bestLineElRef.current && playBtnRef.current) {
      scoreSectionRef.current.style.display = 'block'
      finalScoreElRef.current.textContent = String(finalScoreVal ?? 0)
      bestLineElRef.current.textContent = 'BEST: ' + bestRef.current
      playBtnRef.current.textContent = '▶ PLAY AGAIN'
    }
  }, [])
  const hideOverlay = useCallback(() => { if (overlayRef.current) overlayRef.current.style.display = 'none' }, [])

  const drawFrame = useCallback((gs: GameState, ctx: CanvasRenderingContext2D) => {
    gs.draw(ctx)
    if (dashFillRef.current && dashLabelRef.current && dashBarRef.current) {
      if (gs.dashCool === 0) { dashLabelRef.current.style.opacity = '1'; dashBarRef.current.style.opacity = '0.4'; dashFillRef.current.style.width = '100%' }
      else { dashLabelRef.current.style.opacity = '0.3'; dashBarRef.current.style.opacity = '1'; dashFillRef.current.style.width = ((1 - gs.dashCool / 90) * 100) + '%' }
    }
  }, [])

  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current, gs = gsRef.current
    if (!canvas || !gs || phaseRef.current !== 'playing') return
    const ctx = canvas.getContext('2d'); if (!ctx) return
    gs.update()
    const hit = gs.checkCollision()
    if (hit) {
      setLives(gs.lives); flashDanger()
      if (gs.lives <= 0) {
        gs.dead = true; phaseRef.current = 'dead'
        gs.explode(gs.px, gs.py, PINK, 45); gs.explode(gs.px, gs.py, GOLD, 22); gs.shake(32)
        const finalSc = scoreRef.current
        if (finalSc > bestRef.current) { bestRef.current = finalSc; try { localStorage.setItem('parrotsave_best', String(finalSc)) } catch (_) {} }
        if (bestElRef.current) bestElRef.current.textContent = String(bestRef.current)
        ctx.save(); ctx.translate(Math.round(gs.shakeX), Math.round(gs.shakeY)); drawFrame(gs, ctx); ctx.restore()
        submitScoreRef.current('parrot-save', finalSc)  // ADD — before the timeout
        setTimeout(() => showOverlay(true, finalSc), 900)
        return
      }
    }
    if (gs.frame % 60 === 0) {
      scoreRef.current += gs.mult * gs.cfg.scoreMulti; gs.score = scoreRef.current
      if (scoreRef.current > 0 && scoreRef.current % (10 * gs.cfg.scoreMulti) === 0 && gs.mult === 1) { gs.mult = 2; gs.multTimer = 300; setMult(2) }
      else if (gs.multTimer === 0 && gs.mult > 1) setMult(1)
      if (scoreElRef.current) scoreElRef.current.textContent = String(scoreRef.current)
    }
    ctx.save(); ctx.translate(Math.round(gs.shakeX), Math.round(gs.shakeY)); drawFrame(gs, ctx); ctx.restore()
    animRef.current = requestAnimationFrame(gameLoop)
  }, [drawFrame, flashDanger, setLives, setMult, showOverlay])  // submitScoreRef is stable, no need to add

  const startGame = useCallback(() => {
    cancelAnimationFrame(animRef.current)
    const canvas = canvasRef.current; if (!canvas) return
    const levelKey = selectedLevelRef.current, cfg = LEVELS[levelKey]
    scoreRef.current = 0
    if (scoreElRef.current) scoreElRef.current.textContent = '0'
    setLives(cfg.lives); setMult(1); hideOverlay()
    if (scoreSectionRef.current) scoreSectionRef.current.style.display = 'none'
    if (levelBadgeRef.current) { levelBadgeRef.current.textContent = cfg.label; levelBadgeRef.current.style.color = cfg.color; levelBadgeRef.current.style.borderColor = cfg.color }
    gsRef.current = new GameState(canvas.width, canvas.height, levelKey)
    phaseRef.current = 'playing'
    animRef.current = requestAnimationFrame(gameLoop)
  }, [gameLoop, hideOverlay, setLives, setMult])

  const selectLevel = useCallback((level: LevelKey) => { selectedLevelRef.current = level; setSelectedLevel(level) }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const gs = gsRef.current; if (!gs || phaseRef.current !== 'playing') return
      gs.keys[e.key] = true
      if (e.key === ' ' || e.key === 'w' || e.key === 'W') { e.preventDefault(); gs.dash() }
    }
    const onKeyUp = (e: KeyboardEvent) => { const gs = gsRef.current; if (gs) gs.keys[e.key] = false }
    window.addEventListener('keydown', onKeyDown); window.addEventListener('keyup', onKeyUp)
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp) }
  }, [])

  useEffect(() => {
    const onResize = () => {
      const canvas = canvasRef.current; if (!canvas) return
      canvas.width = window.innerWidth; canvas.height = window.innerHeight
      gsRef.current?.resize(window.innerWidth, window.innerHeight)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => { const gs = gsRef.current; if (!gs || phaseRef.current !== 'playing') return; gs.targetX = clamp(e.clientX, gs.pr, gs.W - gs.pr) }
    const onClick = () => { const gs = gsRef.current; if (!gs || phaseRef.current !== 'playing') return; gs.dash() }
    window.addEventListener('mousemove', onMouseMove); window.addEventListener('click', onClick)
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('click', onClick) }
  }, [])

  useEffect(() => {
    const onTouchMove = (e: TouchEvent) => { const gs = gsRef.current; if (!gs || phaseRef.current !== 'playing') return; e.preventDefault(); gs.targetX = clamp(e.touches[0].clientX, gs.pr, gs.W - gs.pr) }
    const onTouchStart = (e: TouchEvent) => { const gs = gsRef.current; if (!gs || phaseRef.current !== 'playing') return; const t = e.touches[0]; if (t.clientX < window.innerWidth / 2) gs.keys['ArrowLeft'] = true; else gs.keys['ArrowRight'] = true }
    const onTouchEnd = () => { const gs = gsRef.current; if (!gs) return; gs.keys['ArrowLeft'] = false; gs.keys['ArrowRight'] = false }
    window.addEventListener('touchmove', onTouchMove, { passive: false })
    window.addEventListener('touchstart', onTouchStart, { passive: false })
    window.addEventListener('touchend', onTouchEnd)
    return () => { window.removeEventListener('touchmove', onTouchMove); window.removeEventListener('touchstart', onTouchStart); window.removeEventListener('touchend', onTouchEnd) }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (canvas) { canvas.width = window.innerWidth; canvas.height = window.innerHeight }
    try { const saved = parseInt(localStorage.getItem('parrotsave_best') || '0', 10); bestRef.current = isNaN(saved) ? 0 : saved; if (bestElRef.current) bestElRef.current.textContent = String(bestRef.current) } catch (_) {}
    return () => cancelAnimationFrame(animRef.current)
  }, [])

  const levelBtnStyle = (key: LevelKey): React.CSSProperties => {
    const cfg = LEVELS[key], active = selectedLevel === key
    return {
      background: active ? cfg.color : 'transparent',
      color: active ? (key === 'hard' ? '#fff' : '#0a0008') : cfg.color,
      border: `1.5px solid ${cfg.color}`,
      padding: '12px 28px', fontFamily: mono, fontSize: 13, fontWeight: 700,
      letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', borderRadius: 5, transition: 'all 0.15s',
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', background: BG }}>
      <canvas ref={canvasRef} style={{ display: 'block', position: 'absolute', inset: 0 }} />

      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 18px',
        background: 'linear-gradient(180deg, rgba(10,0,8,0.97) 0%, transparent 100%)',
        zIndex: 10, pointerEvents: 'none', fontFamily: mono,
      }}>
        {([
          { label: 'SCORE', ref: scoreElRef, init: '0', gold: false },
          { label: 'BEST', ref: bestElRef, init: '0', gold: true },
          { label: 'LIVES', ref: livesElRef, init: '♥ ♥ ♥', gold: false },
        ] as const).map(({ label, ref, init, gold }) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span style={{ fontSize: 9, color: 'rgba(255,38,128,0.6)', letterSpacing: 2 }}>{label}</span>
            <span ref={ref as React.RefObject<HTMLSpanElement>}
              style={{ fontSize: 20, fontWeight: 700, color: gold ? GOLD : '#fff', letterSpacing: 1 }}>
              {init}
            </span>
          </div>
        ))}
      </div>

      <div ref={multElRef} style={{ position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)', fontSize: 12, color: GOLD, letterSpacing: 2, zIndex: 10, pointerEvents: 'none', opacity: 0, transition: 'opacity 0.3s', fontFamily: mono }} />
      <div ref={dangerElRef} style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 8, border: '0px solid #ff2680', opacity: 0, transition: 'border-width 0.1s, opacity 0.2s' }} />

      <div style={{ position: 'fixed', bottom: 14, left: 14, zIndex: 10, pointerEvents: 'none', fontFamily: mono }}>
        <span ref={dashLabelRef} style={{ fontSize: 9, color: GOLD, letterSpacing: 2, opacity: 0.3, display: 'block', marginBottom: 3, transition: 'opacity 0.2s' }}>DASH READY [SPACE]</span>
        <div ref={dashBarRef} style={{ width: 76, height: 5, background: 'rgba(255,255,255,0.12)', borderRadius: 3 }}>
          <div ref={dashFillRef} style={{ height: '100%', width: '100%', background: GOLD, borderRadius: 3, transition: 'width 0.05s linear' }} />
        </div>
      </div>

      <div ref={levelBadgeRef} style={{ position: 'fixed', bottom: 14, right: 14, zIndex: 10, pointerEvents: 'none', fontSize: 10, letterSpacing: 2, padding: '4px 10px', borderRadius: 4, border: '1px solid', fontFamily: mono }} />

      <button
        onClick={() => { cancelAnimationFrame(animRef.current); navigate(-1) }}
        style={{
          position: 'fixed', top: 12, left: 14, zIndex: 15,
          background: 'transparent', border: '1px solid rgba(255,38,128,0.25)',
          color: 'rgba(255,38,128,0.5)', fontFamily: mono, fontSize: 9,
          letterSpacing: 2, padding: '5px 12px', borderRadius: 3, cursor: 'pointer',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { const b = e.currentTarget; b.style.color = PINK; b.style.borderColor = PINK }}
        onMouseLeave={e => { const b = e.currentTarget; b.style.color = 'rgba(255,38,128,0.5)'; b.style.borderColor = 'rgba(255,38,128,0.25)' }}
      >
        ← GAMES
      </button>

      <div ref={overlayRef} style={{
        position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'rgba(10,0,8,0.93)', zIndex: 20, fontFamily: mono,
      }}>
        <div style={{
          fontFamily: "'Arial Black', sans-serif",
          fontSize: 'clamp(44px,12vw,84px)', letterSpacing: 5,
          background: 'linear-gradient(135deg, #ff2680, #ff6eb4, #ffd700)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          lineHeight: 1, marginBottom: 6, textAlign: 'center',
        }}>
          PARROT<br />SAVE
        </div>
        <div style={{ fontSize: 'clamp(10px,2.5vw,13px)', color: 'rgba(255,38,128,0.6)', letterSpacing: 3, marginBottom: 28, textAlign: 'center' }}>
          THE 10K SQUAD · DODGE OR DIE
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap', justifyContent: 'center' }}>
          {(['easy', 'medium', 'hard'] as LevelKey[]).map(key => (
            <button key={key} style={levelBtnStyle(key)} onClick={() => selectLevel(key)}
              onMouseEnter={e => { if (selectedLevel !== key) (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)' }}>
              {LEVELS[key].label}
            </button>
          ))}
        </div>
        <div ref={scoreSectionRef} style={{ textAlign: 'center', marginBottom: 26, display: 'none' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,38,128,0.6)', letterSpacing: 3, marginBottom: 4 }}>YOUR SCORE</div>
          <div ref={finalScoreElRef} style={{ fontSize: 'clamp(44px,10vw,72px)', fontWeight: 700, color: '#fff', lineHeight: 1, marginBottom: 2 }}>0</div>
          <div ref={bestLineElRef} style={{ fontSize: 13, color: GOLD, letterSpacing: 2 }}>BEST: 0</div>
        </div>
        <button ref={playBtnRef} onClick={startGame}
          style={{ background: PINK, color: '#fff', border: 'none', padding: '14px 44px', fontFamily: mono, fontSize: 15, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', cursor: 'pointer', borderRadius: 4, marginBottom: 14, transition: 'background 0.15s, transform 0.1s' }}
          onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#ff6eb4'; b.style.transform = 'scale(1.04)' }}
          onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = PINK; b.style.transform = 'scale(1)' }}
          onMouseDown={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.97)' }}
          onMouseUp={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.04)' }}
        >
          ▶ PLAY NOW
        </button>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', letterSpacing: 1, textAlign: 'center', lineHeight: 2 }}>
          ARROWS / WASD · SPACE to dash<br />Mouse to aim · Mobile: tap left/right
        </div>
        <button
          onClick={() => { cancelAnimationFrame(animRef.current); navigate(-1) }}
          style={{ marginTop: 20, background: 'transparent', border: '1px solid rgba(255,38,128,0.2)', color: 'rgba(255,38,128,0.4)', fontFamily: mono, fontSize: 9, letterSpacing: 2, padding: '6px 16px', borderRadius: 3, cursor: 'pointer', transition: 'all 0.15s' }}
          onMouseEnter={e => { const b = e.currentTarget; b.style.color = PINK; b.style.borderColor = PINK }}
          onMouseLeave={e => { const b = e.currentTarget; b.style.color = 'rgba(255,38,128,0.4)'; b.style.borderColor = 'rgba(255,38,128,0.2)' }}
        >
          ← BACK TO GAMES
        </button>
      </div>
    </div>
  )
}